import { useEffect, useRef, useState, useCallback } from "react";
import { api, fileSrc, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MessageSquare, Send, Loader2, Users, Trash2, Flag, ImagePlus, ShieldOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const initials = (s) => (s || "?").slice(0, 2).toUpperCase();
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

function Avatar({ name, logo, size = 40 }) {
  return logo ? (
    <img src={fileSrc(logo)} alt={name} className="rounded-full object-cover border border-slate-200 shrink-0" style={{ width: size, height: size }} />
  ) : (
    <div className="rounded-full bg-emerald-500/20 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0" style={{ width: size, height: size }}>{initials(name)}</div>
  );
}

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSuper, setIsSuper] = useState(false);
  const [members, setMembers] = useState({ members: [], count: 0, online: 0 });
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const lastRef = useRef(null);
  const bootRef = useRef(true);

  const scrollBottom = () => { requestAnimationFrame(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; }); };

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/messages", { params: lastRef.current ? { after: lastRef.current } : {} });
      setIsSuper(data.is_super_admin);
      if (data.messages.length) {
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const merged = [...prev];
          data.messages.forEach((m) => { if (!seen.has(m.id)) merged.push(m); else { const i = merged.findIndex((x) => x.id === m.id); if (i >= 0) merged[i] = m; } });
          return merged;
        });
        lastRef.current = data.messages[data.messages.length - 1].created_at;
        scrollBottom();
      }
      api.post("/chat/mark-read").catch(() => {});
    } catch (e) { /* silent on poll */ } finally { if (bootRef.current) { setLoading(false); bootRef.current = false; scrollBottom(); } }
  }, []);

  const loadMembers = useCallback(async () => {
    try { const { data } = await api.get("/chat/members"); setMembers(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    poll(); loadMembers();
    const t = setInterval(poll, 4000);
    const tm = setInterval(loadMembers, 15000);
    return () => { clearInterval(t); clearInterval(tm); };
  }, [poll, loadMembers]);

  const attach = async (files) => {
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { url: data.url, path: data.path, filename: data.filename }]);
      } catch { toast.error("Échec de l'envoi de la photo"); }
    }
  };

  const send = async () => {
    if (!text.trim() && photos.length === 0) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/messages", { text, photos });
      setMessages((prev) => [...prev, data]);
      lastRef.current = data.created_at;
      setText(""); setPhotos([]); scrollBottom();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSending(false); }
  };

  const del = async (m) => {
    try { await api.delete(`/chat/messages/${m.id}`); setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, deleted: true, text: "Message supprimé", photos: [], can_delete: false } : x)); toast.success("Message supprimé"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const report = async (m) => {
    try { await api.post(`/chat/messages/${m.id}/report`); toast.success("Message signalé à la modération"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleBan = async (mem) => {
    try { await api.post("/chat/ban", { company_id: mem.company_id, banned: !mem.banned }); toast.success(mem.banned ? "Accès rétabli" : "Entreprise suspendue du chat"); loadMembers(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const MembersPanel = (
    <div className="space-y-2" data-testid="chat-members-list">
      {members.members.map((m) => (
        <div key={m.company_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50" data-testid={`chat-member-${m.company_id}`}>
          <div className="relative">
            <Avatar name={m.name} logo={m.logo} size={36} />
            <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${m.online ? "bg-emerald-500" : "bg-slate-300"}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 truncate">{m.name}</div>
            <div className="text-[11px] text-slate-500">{m.online ? "En ligne" : m.last_active ? `Vu ${fmtTime(m.last_active)}` : "—"}</div>
          </div>
          {isSuper && (
            <Button size="icon" variant="ghost" onClick={() => toggleBan(m)} data-testid={`chat-ban-${m.company_id}`} className={m.banned ? "text-emerald-600" : "text-red-500"} title={m.banned ? "Rétablir" : "Suspendre"}>
              {m.banned ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
            </Button>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <PageHeader title="Chat communautaire" subtitle="Échangez entre professionnels du detailing — aucune donnée client n'est partagée" icon={MessageSquare}
        actions={
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" data-testid="chat-members-btn" className="border-slate-200"><Users className="w-4 h-4 mr-1.5" /> Membres ({members.count}) · <span className="text-emerald-600 ml-1">{members.online} en ligne</span></Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white border-slate-200 w-80">
              <SheetHeader><SheetTitle className="text-slate-900">Membres de la communauté</SheetTitle></SheetHeader>
              <div className="mt-4 overflow-y-auto ced-scroll-thin max-h-[calc(100vh-120px)]">{MembersPanel}</div>
            </SheetContent>
          </Sheet>
        }
      />

      <div className="bg-white border border-slate-200 rounded-2xl flex flex-col overflow-hidden" style={{ height: "calc(100vh - 220px)", minHeight: 420 }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto ced-scroll-thin p-4 space-y-4" data-testid="chat-scroll">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
          ) : messages.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-16">Aucun message pour l'instant. Lancez la discussion 👋</div>
          ) : messages.map((m) => {
            const mine = m.is_mine;
            return (
              <div key={m.id} className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.id}`}>
                <Avatar name={m.company_name} logo={m.avatar_url} size={36} />
                <div className={`max-w-[78%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold text-slate-900 truncate">{m.company_name}</span>
                    <span className="text-[10px] text-slate-400">{fmtTime(m.created_at)}</span>
                  </div>
                  <div className={`rounded-2xl px-3.5 py-2 text-sm ${m.deleted ? "bg-slate-100 text-slate-400 italic" : mine ? "bg-emerald-500 text-black" : "bg-slate-100 text-slate-800"}`}>
                    {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                    {(m.photos || []).length > 0 && (
                      <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                        {m.photos.map((p, i) => <a key={i} href={fileSrc(p.url || p.path)} target="_blank" rel="noreferrer"><img src={fileSrc(p.url || p.path)} alt="" className="rounded-lg max-h-40 object-cover w-full" /></a>)}
                      </div>
                    )}
                  </div>
                  {!m.deleted && (
                    <div className={`flex gap-2 mt-1 ${mine ? "justify-end" : ""}`}>
                      {m.can_delete && <button onClick={() => del(m)} data-testid={`chat-delete-${m.id}`} className="text-[11px] text-slate-400 hover:text-red-500 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Supprimer</button>}
                      {!mine && <button onClick={() => report(m)} data-testid={`chat-report-${m.id}`} className="text-[11px] text-slate-400 hover:text-amber-600 flex items-center gap-1"><Flag className="w-3 h-3" /> Signaler</button>}
                      {isSuper && (m.report_count > 0) && <span className="text-[11px] text-amber-600">⚠ {m.report_count} signalement(s)</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-slate-200 p-3">
          {photos.length > 0 && (
            <div className="flex gap-2 mb-2 flex-wrap" data-testid="chat-pending-photos">
              {photos.map((p, i) => (
                <div key={i} className="relative w-14 h-14 rounded-lg overflow-hidden border border-slate-200">
                  <img src={fileSrc(p.url)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setPhotos((x) => x.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/70 text-red-500 rounded-bl px-1 text-xs">×</button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files && attach(e.target.files)} />
            <Button variant="outline" size="icon" onClick={() => fileRef.current?.click()} data-testid="chat-attach" className="border-slate-200 shrink-0"><ImagePlus className="w-4 h-4" /></Button>
            <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Écrivez un message à la communauté…" className="bg-slate-50 border-slate-200" />
            <Button onClick={send} disabled={sending} data-testid="chat-send" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold shrink-0">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
